////////////////////////////////////////////
// NETWORK RESOLUTION — THE SINGLE ?net= URL CONTRACT
////////////////////////////////////////////
//
// PURE FUNCTIONS (NO REACT). ONE PARAM CARRIES PLATFORM + SUB-NET:
//
//   ?net= ∈ mainnet (OMITTED — CANONICAL URLS HAVE NO PARAM) | devnet |
//          rh | rh-testnet — PLATFORM IS DERIVED (rh* = ROBINHOOD CHAIN)
//
//   LAUNCH KEY SHAPE IS AUTHORITATIVE FOR PLATFORM ON DETAIL PAGES
//   (0xfactory:id = RH, base58 = SOLANA) — A SHARED LINK ALWAYS OPENS ON
//   THE CHAIN THE LAUNCH LIVES ON, WHATEVER ELSE THE URL SAYS.
//
//   LEGACY ?chain=solana|rh LINKS ARE READ FOREVER (PRECEDENCE: KEY >
//   ?chain > ?net) BUT NEVER EMITTED — PageShell CANONICALIZES THEM AWAY.
//
// NOTHING IS REMEMBERED: NO STICKY PREFERENCE, NO RELOADS — THE URL SAYS
// EVERYTHING AND EVERY MODULE RE-DERIVES FROM IT REACTIVELY.
import {
  RH_CHAIN_ID_MAINNET, RH_CHAIN_ID_TESTNET,
  RH_EXPLORER_MAINNET, RH_EXPLORER_TESTNET,
  RH_PUBLIC_RPC_MAINNET, RH_PUBLIC_RPC_TESTNET,
  RH_FUZE_ADDRESS_MAINNET, RH_FUZE_ADDRESS_TESTNET,
  RH_FAIR_FACTORIES_MAINNET, RH_FAIR_FACTORIES_TESTNET,
  RH_INSTANT_V2_ADDRESS_MAINNET, RH_INSTANT_V2_ADDRESS_TESTNET,
  RH_FEE_LOCKER_V2_ADDRESS_MAINNET, RH_FEE_LOCKER_V2_ADDRESS_TESTNET,
  RH_WETH_MAINNET, RH_WETH_TESTNET,
  RH_SWAP_ROUTER_MAINNET, RH_SWAP_ROUTER_TESTNET,
  RH_QUOTER_V2_MAINNET, RH_QUOTER_V2_TESTNET,
  RH_AUCTION_V3_ADDRESS_MAINNET, RH_AUCTION_V3_ADDRESS_TESTNET,
  RH_V4_POSITION_MANAGER,
} from "@/config/protocol";

// THE FOUR CANONICAL NETS — SAME VALUE SPACE THE SERVER'S validate.mjs
// ALLOWLISTS AND THE ADMIN PAGES ALREADY USE
export type NetId = "mainnet" | "devnet" | "rh" | "rh-testnet";
export type SolNet = "mainnet" | "devnet";
export type RhNet = "rh" | "rh-testnet";
export type Chain = "solana" | "rh";

// LAUNCH KEY SHAPE — MUST MATCH THE SERVER'S shared/rh-constants.mjs::RH_LAUNCH_KEY_RE
// EXACTLY (BOUNDED {1,20} DIGITS) SO CLIENT AND SERVER NEVER DISAGREE
export const RH_LAUNCH_KEY_RE = /^0x[a-fA-F0-9]{40}:[0-9]{1,20}$/;

// V3 AUCTION-CURVE LAUNCH KEY — A BARE FuzeAuction ADDRESS, NO COLON (V3 has
// no enumerable per-factory id at all — see docs/auction-curve-frontend-plan.md
// discrepancy #3 and web/server/lib/db.mjs's auction_v3_launches PRIMARY KEY).
// MUST BE CHECKED BEFORE RH_LAUNCH_KEY_RE EVERYWHERE THE CHAIN IS
// DISCRIMINATED — a bare 0x address can't match the colon shape so ORDER
// doesn't actually matter for THESE TWO, but it must be checked before ANY
// base58/Solana fallback (a bare 0x never matches base58 either, so in
// practice this only needs to exist as its own explicit branch, not win a
// priority fight).
export const RH_V3_KEY_RE = /^0x[a-fA-F0-9]{40}$/;

// PLATFORM TABLE — SELECTOR TABS, MENU ROWS, AND SUB-NET PILLS ALL DRIVE
// OFF THIS (GENERALIZED FROM THE ADMIN MARKET_TABS)
export interface PlatformTab {
  key: Chain;
  label: string;
  short: string;
  sub: string;
  // VENUE ICON (pump.fun / ROBINHOOD) — PRODUCT CONTEXTS (SELECTOR, CARDS)
  icon: string;
  // NATIVE CHAIN ICON (SOLANA / ETHEREUM) — WALLET/ACCOUNT CONTEXTS
  chainIcon: string;
  chainName: string;
  nets: { key: NetId; label: string }[];
}

export const PLATFORM_TABS: PlatformTab[] = [
  {
    key: "solana", label: "pump.fun", short: "pump.fun",
    sub: "Solana · buy & launch with SOL", icon: "/pump-fun.svg",
    chainIcon: "/solana.svg", chainName: "Solana",
    nets: [{ key: "mainnet", label: "mainnet" }, { key: "devnet", label: "devnet" }],
  },
  {
    key: "rh", label: "Robinhood Chain", short: "Robinhood",
    sub: "buy & launch with ETH", icon: "/robinhood-icon.png",
    chainIcon: "/ethereum.svg", chainName: "Robinhood Chain",
    nets: [{ key: "rh", label: "mainnet" }, { key: "rh-testnet", label: "testnet" }],
  },
];

export function chainOf(net: NetId): Chain {

  return net === "rh" || net === "rh-testnet" ? "rh" : "solana";
}

export function isTestNet(net: NetId): boolean {

  return net === "devnet" || net === "rh-testnet";
}

// LAUNCH VENUE BY KEY SHAPE — THE THREE-WAY DISCRIMINATOR LaunchPage AND THE
// LINK BUILDERS DISPATCH ON. "rh-v3" (bare 0x, auction-curve) IS CHECKED
// BEFORE "rh" (colon-shaped V1/V2/instant) SO A V3 KEY NEVER FALLS THROUGH TO
// THE FAIR/INSTANT DETAIL PATH; A BARE 0x CAN'T MATCH base58 EITHER SO
// "solana" IS SAFELY THE FALLBACK.
export type LaunchVenue = "rh-v3" | "rh" | "solana";

export function launchVenue(key: string): LaunchVenue {

  if (RH_V3_KEY_RE.test(key)) return "rh-v3";
  if (RH_LAUNCH_KEY_RE.test(key)) return "rh";
  return "solana";
}

////////////////////////////////////////////
// RESOLUTION — KEY SHAPE > LEGACY ?chain > ?net
////////////////////////////////////////////

export function resolveNetId(search: URLSearchParams): NetId {

  const netParam = search.get("net");
  let net: NetId =
    netParam === "devnet" || netParam === "rh" || netParam === "rh-testnet" ? netParam
    // DEVNET RPC HINT — KEPT FROM THE VANILLA CONTRACT
    : netParam !== "mainnet" && /devnet/.test(search.get("rpc") || "") ? "devnet"
    : "mainnet";

  // LEGACY ?chain= — OLD LINKS IN THE WILD RESOLVE FOREVER, NEVER EMITTED.
  // SAME SEMANTICS THE TWO-PARAM MODEL HAD: chain PICKS THE PLATFORM, THE
  // OTHER PLATFORM'S SUB-NET VALUE IS MEANINGLESS AND FALLS TO MAINNET.
  const chainParam = search.get("chain");
  if (chainParam === "rh" && chainOf(net) === "solana")
    net = "rh";
  else if (chainParam === "solana" && chainOf(net) === "rh")
    net = "mainnet";

  // LAUNCH KEY SHAPE IS AUTHORITATIVE FOR PLATFORM — SUB-NET TIER SURVIVES
  // ONLY WHEN IT ALREADY BELONGS TO THE KEY'S PLATFORM. A BARE V3 KEY (NO
  // COLON) RESOLVES TO "rh" HERE TOO — venue (rh vs rh-v3) IS A SEPARATE
  // AXIS FROM net/chain, DECIDED BY launchVenue() WHERE IT MATTERS.
  const launchParam = search.get("launch");
  if (launchParam) {

    const keyChain: Chain = RH_LAUNCH_KEY_RE.test(launchParam) || RH_V3_KEY_RE.test(launchParam) ? "rh" : "solana";
    if (keyChain === "rh" && chainOf(net) === "solana")
      net = "rh";
    else if (keyChain === "solana" && chainOf(net) === "rh")
      net = "mainnet";
  }

  return net;
}

// RESOLVED NETWORK STATE — EVERYTHING A PAGE NEEDS, DERIVED FROM THE URL
export interface Network {
  net: SolNet;
  isDevnet: boolean;
  chain: Chain;
  isRh: boolean;
  rhNet: RhNet;
  rhIsTestnet: boolean;
  // ACTIVE NET — THE ONE STRING /api/* CALLS TAKE
  activeNet: NetId;
  // NAV SUFFIX — CARRIES THE ACTIVE NET SO NAVIGATION NEVER DROPS IT
  navQuery: string;
  // RH DEPLOYMENT PINS FOR THE ACTIVE RH NET
  rh: {
    chainId: number;
    explorer: string;
    publicRpc: string;
    // CREATE TARGET — v2 where deployed, else v1 (see protocol.ts's U-5 flip
    // comment). NEVER compare a launch key's factory against this alone —
    // use fairFactories, which covers every version ever deployed.
    fuzeAddress: `0x${string}`;
    // EVERY FAIR FACTORY EVER DEPLOYED ON THIS NET — the allowlist for
    // routing a post-creation write (commit) to a launch key's own factory.
    fairFactories: readonly `0x${string}`[];
    instantV2Address: `0x${string}`;
    feeLockerV2Address: `0x${string}`;
    weth: `0x${string}`;
    swapRouter: `0x${string}`;
    routerIs02: boolean;
    quoterV2: `0x${string}`;
    // FuzeFactoryV3 (auction-curve create target) — EMPTY STRING ON MAINNET
    // UNTIL U-A12's CEREMONY; CALLERS GATE ON THIS BEING TRUTHY, SAME AS
    // fuzeAddress's PRE-U-5 "" PLACEHOLDER PATTERN.
    v3Factory: `0x${string}` | "";
    // v4 PERIPHERY PositionManager — THE NFT CONTRACT A GRADUATED/SUNSET V3
    // CURVE'S LOCKED POSITION IS MINTED UNDER (U-F5's "locked-pool proof"
    // explorer link). SAME ADDRESS ON BOTH RH NETS — SEE RH_V4_POSITION_MANAGER's
    // OWN COMMENT.
    v4PositionManager: `0x${string}`;
  };
}

export function resolveNetwork(search: URLSearchParams): Network {

  const activeNet = resolveNetId(search);
  const chain = chainOf(activeNet);
  const isRh = chain === "rh";
  // TEST NETS PAIR — EITHER TEST NET PUTS BOTH CHAINS IN TEST MODE, SO
  // MIXED-CHAIN SURFACES (THE LAUNCHPAD) NEVER BLEND TEST AND REAL MONEY
  const test = isTestNet(activeNet);
  const net: SolNet = test ? "devnet" : "mainnet";
  const rhNet: RhNet = test ? "rh-testnet" : "rh";
  const rhIsTestnet = rhNet === "rh-testnet";

  return {
    net,
    isDevnet: net === "devnet",
    chain,
    isRh,
    rhNet,
    rhIsTestnet,
    activeNet,
    navQuery: activeNet === "mainnet" ? "" : "?net=" + activeNet,
    rh: rhIsTestnet
      ? {
          chainId: RH_CHAIN_ID_TESTNET, explorer: RH_EXPLORER_TESTNET, publicRpc: RH_PUBLIC_RPC_TESTNET,
          fuzeAddress: RH_FUZE_ADDRESS_TESTNET as `0x${string}`,
          fairFactories: RH_FAIR_FACTORIES_TESTNET as readonly `0x${string}`[],
          instantV2Address: RH_INSTANT_V2_ADDRESS_TESTNET as `0x${string}`,
          feeLockerV2Address: RH_FEE_LOCKER_V2_ADDRESS_TESTNET as `0x${string}`,
          weth: RH_WETH_TESTNET as `0x${string}`,
          swapRouter: RH_SWAP_ROUTER_TESTNET as `0x${string}`,
          routerIs02: false,
          quoterV2: RH_QUOTER_V2_TESTNET as `0x${string}`,
          v3Factory: RH_AUCTION_V3_ADDRESS_TESTNET as `0x${string}`,
          v4PositionManager: RH_V4_POSITION_MANAGER as `0x${string}`,
        }
      : {
          chainId: RH_CHAIN_ID_MAINNET, explorer: RH_EXPLORER_MAINNET, publicRpc: RH_PUBLIC_RPC_MAINNET,
          fuzeAddress: RH_FUZE_ADDRESS_MAINNET as `0x${string}`,
          fairFactories: RH_FAIR_FACTORIES_MAINNET as readonly `0x${string}`[],
          instantV2Address: RH_INSTANT_V2_ADDRESS_MAINNET as `0x${string}`,
          feeLockerV2Address: RH_FEE_LOCKER_V2_ADDRESS_MAINNET as `0x${string}`,
          weth: RH_WETH_MAINNET as `0x${string}`,
          swapRouter: RH_SWAP_ROUTER_MAINNET as `0x${string}`,
          routerIs02: true,
          quoterV2: RH_QUOTER_V2_MAINNET as `0x${string}`,
          v3Factory: RH_AUCTION_V3_ADDRESS_MAINNET as `0x${string}` | "",
          v4PositionManager: RH_V4_POSITION_MANAGER as `0x${string}`,
        },
  };
}

////////////////////////////////////////////
// LINKS — MAINNET OMITS ?net= (CANONICAL URL)
////////////////////////////////////////////

export function netHref(base: string, net: NetId, params?: Record<string, string>): string {

  const p = new URLSearchParams();
  for (const k in params || {}) {

    if (params![k]) p.set(k, params![k]);
  }
  if (net !== "mainnet") p.set("net", net);
  const s = p.toString();
  return base + (s ? "?" + s : "");
}

// CANONICAL RACE-PAGE LINKS — THE INDEXABLE /launch/<key> FORM (THE SERVER
// RENDERS OG META ON THAT PATH SO LINKS UNFURL; THE LEGACY /?launch= QUERY
// FORM STILL RESOLVES FOR OLD LINKS)
export function raceUrl(launch: string, isDevnet: boolean): string {

  return "/launch/" + encodeURIComponent(launch) + (isDevnet ? "?net=devnet" : "");
}

export function rhRaceUrl(launchKey: string, rhIsTestnet: boolean): string {

  return "/launch/" + encodeURIComponent(launchKey) + "?net=" + (rhIsTestnet ? "rh-testnet" : "rh");
}
